import { useCallback } from 'react';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import type { FilterStatesKey } from '@modules-rocco/filters/types/filter-states';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { useLocation, useNavigate } from '@remix-run/react';
import { mapFilterStatesToURL } from '@modules-rocco/filters/data-mappers/filter-states-to-url';
import { useSorts } from '@ui-rocco/hooks/use-sorts';
import { useAggregation } from '@ui-rocco/hooks/use-aggregation';
import { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { toggleMultipleListFilterStateItem } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import { toggleSearchableListFilterStateItem } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import { useCurrentLocation } from '@ui-rocco/hooks/location/use-current-location';
import { usePlaceFinder } from '@ui-rocco/hooks/location/use-place-finder';
import type { SupportedCountry } from 'generated/graphql';
import { PermissionEnum } from 'generated/graphql';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isMultipleValuesFilterState,
    isSearchQueryFilterState,
    isSearchableListFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import { isSingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import { useRootData } from '@modules/root';
import { useUser } from '~/modules/user/hook/use-user';
import { getPermissions } from '@modules/user';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';

export const useCategoryFilterSort = (
    filtersConfig: FiltersConfig,
    searchIndex: SearchIndexes | undefined,
    initialFilterStates: FilterStates
) => {
    const { localisation } = useRootData();

    // get user, we need to check if user can access additional filters
    const user = useUser();
    const hasMarketingView = getPermissions(
        user,
        PermissionEnum.BmMarketingView
    );

    // returns allowed sorts for search index
    const sortOptions = useSorts(searchIndex ?? null, false);

    // web-platform specific filter navigation
    const { handleRefetch } = useFiltersNavigation({
        defaultSort: (sortOptions.find((o) => o.default) ?? sortOptions[0])
            ?.value,
    });

    // web-platform specific aggregation hook
    const { handleAggregate, resetAggregationQueue } = useAggregation({
        searchIndex: searchIndex,
    });

    // web-platform current location fetcher
    const {
        currentLocation,
        getCurrentLocation,
        loading: currentLocationLoading,
    } = useCurrentLocation();

    // web-platform keyword location fetcher
    const {
        places,
        handlePlaceFind,
        resetPlaces,
        loading: placeFinderLoading,
    } = usePlaceFinder({
        country: localisation.country as SupportedCountry,
    });

    const searchFilters = useSearchFilters({
        searchIndex,
        hasAdminPermission: hasMarketingView,
        config: filtersConfig,
        initialStates: initialFilterStates,
        isCategoryPage: true,
        onRefetch: handleRefetch,
        onAggregate: handleAggregate,
        onAggregateQueueClear: resetAggregationQueue,
        onToggleMultipleValues: toggleMultipleListFilterStateItem,
        onToggleSearchableList: toggleSearchableListFilterStateItem,
        locationService: {
            currentLocation: {
                isLoading: currentLocationLoading,
                location: currentLocation,
                onCurrentLocationFetch: getCurrentLocation,
            },

            keywordLocations: {
                isLoading: placeFinderLoading,
                locations: places,
                onKeywordResetPlaces: resetPlaces,
                onKeywordLocationsFetch: handlePlaceFind,
            },
        },
    });

    // filter disable handler
    const handleDisableFilter = useCallback(
        (
            key: string,
            state: FilterState,
            listItem?: SingleValueFilterState
        ) => {
            const filterStates = searchFilters.filterStates;

            if (isSingleValueFilterState(state) && state.checked) {
                searchFilters.updateFilter(
                    {
                        ...filterStates,
                        states: {
                            ...filterStates.states,
                            [key]: {
                                ...state,
                                checked: false,
                            },
                        },
                    },
                    true
                );
            } else if (
                isMultipleValuesFilterState(state) &&
                state.selectedItems.length > 0 &&
                listItem
            ) {
                searchFilters.updateFilter(
                    {
                        ...filterStates,
                        states: {
                            ...filterStates.states,
                            [key]: searchFilters.handleToggleMultipleValues(
                                state,
                                listItem,
                                false
                            ),
                        },
                    },
                    true
                );
            } else if (
                isSearchableListFilterState(state) &&
                state.selectedItems.length > 0 &&
                listItem
            ) {
                searchFilters.updateFilter(
                    {
                        ...filterStates,
                        states: {
                            ...filterStates.states,
                            [key]: searchFilters.handleToggleSearchableList(
                                state,
                                listItem,
                                false
                            ),
                        },
                    },
                    true
                );
            } else if (
                isSearchQueryFilterState(state) &&
                state.query.length > 0
            ) {
                searchFilters.updateFilter(
                    {
                        ...filterStates,
                        states: {
                            ...filterStates.states,
                            [key]: {
                                ...state,
                                query: '',
                            },
                        },
                    },
                    true
                );
            }
        },
        [searchFilters]
    );

    return { searchFilters, sortOptions, handleDisableFilter };
};

interface UseFilterNavigationHookOptions {
    defaultSort?: SearchSortOptions;
    ignoreFilterKeys?: FilterStatesKey[];
    preventScrollReset?: boolean;
}

const useFiltersNavigation = (props: UseFilterNavigationHookOptions = {}) => {
    const {
        ignoreFilterKeys = [],
        preventScrollReset = true,
        defaultSort,
    } = props;

    const location = useLocation();
    const navigate = useNavigate();
    const user = useUser();

    const handleRefetch = useCallback(
        (states: FilterStates) => {
            const originalURL = location.pathname + location.search;
            const newURL = mapFilterStatesToURL(
                states,
                originalURL,
                ignoreFilterKeys,
                defaultSort,
                user.FeatureFlags
            );

            if (newURL === originalURL) return;

            // applied filters change, reload data
            navigate(newURL, { preventScrollReset });
        },
        [
            location.pathname,
            location.search,
            ignoreFilterKeys,
            navigate,
            preventScrollReset,
            defaultSort,
            user.FeatureFlags,
        ]
    );

    return { handleRefetch };
};
